import { useCallback, useEffect, useRef, useState } from "react";

import type { TransactionState, TransactionStep } from "../types/transaction";
import { INITIAL_TRANSACTION_STATE } from "../types/transaction";

import type { ScheduleSet } from "./schedules";
import type { SimScenario, SimulatedPhase } from "./types";

/**
 * Generic phase-driven simulated transaction state machine.
 *
 * Bridges {@link useFlowTimer}'s phase model (empty → filled →
 * pending → … → success/error) with the {@link TransactionState}
 * surface that real orchestration hooks (useSwap, useStakeTransaction,
 * etc.) expose. Sim orchestrators wrap this
 * to provide hook-shaped return values driven by the schedule.
 *
 * `execute()` kicks off the timer; `reset()` returns to `empty` and
 * clears the schedule. Re-calling `execute()` after `success`/`error`
 * restarts the schedule from `filled`.
 *
 * Why not import `useFlowTimer` directly? — `useFlowTimer` was built
 * for the previous showcase forks and locks in card-height
 * measurement + render-relevant flags. Here we want a pure logic
 * machine; the widgets themselves carry the visual chrome via the
 * real components.
 */
export interface UseSimulatedTransactionResult {
    /** Current scheduled phase. */
    phase: SimulatedPhase;
    /** TransactionState-shaped projection for orchestrator use. */
    state: TransactionState;
    /** Kick off the schedule (or restart it from terminal). */
    execute: () => Promise<void>;
    /** Cancel and return to `empty`. */
    reset: () => void;
}

export interface UseSimulatedTransactionConfig {
    scenario: SimScenario;
    schedule: ScheduleSet;
    /** Override the error message shown when the schedule resolves to `error`. */
    errorMessage?: string;
    /** Override the fake tx signature shown on success. */
    signature?: string;
    /**
     * When true (default), the schedule starts automatically on mount —
     * the widget walks through phases without user interaction.
     * Set false if the consumer wants to drive `execute()` manually
     * (e.g. an interactive sim playground where you want to click
     * submit yourself).
     */
    autoStart?: boolean;
    /**
     * Pause (ms) after the schedule settles on its terminal phase
     * (success/error) before it replays from the start. When omitted or
     * negative, the schedule plays once and stops. Drives the looping
     * BuyWidget/TradeWidget sims (the SimModeProvider forwards its
     * `repeatPauseMs` here via the orchestrators).
     */
    repeatPauseMs?: number;
}

const DEFAULT_ERROR_MESSAGE = "Transaction failed (sim mode)";
const DEFAULT_SIGNATURE = `5sim${"0".repeat(80)}`; // Solana sigs are 88 chars base58; prefix marks sim.

/**
 * Global playback slowdown for the auto-played sims. The schedules in
 * `schedules.ts` were tuned to a cadence that reviewers found too quick to
 * read every phase, so we stretch every phase's arrival time by this factor —
 * each page/phase now dwells ~this-many times longer before the next arrives.
 * A single lever keeps the hand-tuned relative pacing intact; bump or reset it
 * here rather than re-tuning every `t` in `schedules.ts`.
 */
const PLAYBACK_SLOWDOWN = 2;

/**
 * Map a phase to its corresponding TransactionState. Real transaction
 * hooks expose `{isLoading, isSuccess, isError, errorMessage, signature, step}`;
 * the showcase phase model carries more granularity (pending vs receipt
 * vs processing) than `step` does, so we collapse the in-flight phases
 * into the closest `step` value.
 */
function phaseToState(phase: SimulatedPhase, errorMessage: string, signature: string): TransactionState {
    const stepByPhase: Record<SimulatedPhase, TransactionStep> = {
        empty: "idle",
        focused: "idle",
        filled: "idle",
        pending: "building",
        receipt: "signing",
        processing: "confirming",
        success: "complete",
        postSuccess: "complete",
        error: "idle"
    };
    const step = stepByPhase[phase];
    const isLoading = phase === "pending" || phase === "receipt" || phase === "processing";
    const isSuccess = phase === "success" || phase === "postSuccess";
    const isError = phase === "error";
    return {
        isLoading,
        isSuccess,
        isError,
        errorMessage: isError ? errorMessage : null,
        signature: isSuccess ? signature : null,
        step
    };
}

export function useSimulatedTransaction({
    scenario,
    schedule,
    errorMessage = DEFAULT_ERROR_MESSAGE,
    signature = DEFAULT_SIGNATURE,
    autoStart = true,
    repeatPauseMs
}: UseSimulatedTransactionConfig): UseSimulatedTransactionResult {
    const [phase, setPhase] = useState<SimulatedPhase>("empty");
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const clearTimers = useCallback((): void => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];
    }, []);

    useEffect(() => {
        // Clean up any pending timers on unmount or schedule change.
        return clearTimers;
    }, [clearTimers]);

    const execute = useCallback(async (): Promise<void> => {
        if (scenario === "off") return;
        clearTimers();
        // Each step is scheduled from "now" using its `t` offset.
        const steps = scenario === "happy" ? schedule.happy : schedule.error;
        timersRef.current = steps.map(({ p, t }) => setTimeout(() => setPhase(p), t * PLAYBACK_SLOWDOWN));
        // Loop: after the terminal phase holds for `repeatPauseMs`, reset
        // to `empty` and replay the schedule from the top. A negative /
        // omitted value plays once and stops.
        if (repeatPauseMs !== undefined && repeatPauseMs >= 0) {
            const maxT = steps.reduce((m, s) => Math.max(m, s.t), 0);
            timersRef.current.push(
                setTimeout(
                    () => {
                        setPhase("empty");
                        void execute();
                    },
                    maxT * PLAYBACK_SLOWDOWN + repeatPauseMs
                )
            );
        }
    }, [scenario, schedule, clearTimers, repeatPauseMs]);

    // Auto-start the schedule on mount when sim is active. Without
    // this, the widget renders forever at the `empty` phase because
    // nothing calls `execute()` — the real widget's submit handler
    // is gated by other UI state we can't easily satisfy externally
    // (input validation, focus, etc.). Drives the visual flow
    // end-to-end with no user click required.
    //
    // Intentionally fires once per mount, with the mount-time config held
    // in a ref. If `scenario` changes the consumer should remount the
    // subtree (`key={scenario}`) per the SimModeProvider contract.
    const mountStartRef = useRef({ autoStart, scenario, execute });
    useEffect(() => {
        const mount = mountStartRef.current;
        if (!mount.autoStart || mount.scenario === "off") return;
        void mount.execute();
    }, []);

    const reset = useCallback((): void => {
        clearTimers();
        setPhase("empty");
    }, [clearTimers]);

    const state = scenario === "off" ? INITIAL_TRANSACTION_STATE : phaseToState(phase, errorMessage, signature);

    return { phase, state, execute, reset };
}
